import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const defaultAuth = {
  username: '',
  userid: null,
  roles: [],
  memberships: [],
  isPlatformAdmin: false,
  isAdmin: false,
  isSafetyMonitor: false,
  isLoading: true,
};

// Safety Monitor and Admin are held per Operator, in memberships the backend
// reports; Platform Admin spans every Operator, so it counts as both
// (ADR-0012). `roles` lists the roles held for at least one Operator.
const fetchRoles = async (idToken) => {
  const response = await fetch(`${process.env.REACT_APP_SERVERURL}/users/roles`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!response.ok) throw new Error(`Loading roles failed: ${response.status}`);
  return response.json();
};

const rolesFrom = ({ is_platform_admin, memberships }) =>
  is_platform_admin
    ? ['PlatformAdmin', 'Admin', 'SafetyMonitor']
    : [...new Set(memberships.map((membership) => membership.role))];

const useAuth = () => {
  const [auth, setAuth] = useState(defaultAuth);

  useEffect(() => {
    let isMounted = true;

    const loadAuth = async () => {
      let payload;
      let staff = { is_platform_admin: false, memberships: [] };
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        payload = idToken?.payload;
        if (idToken) staff = await fetchRoles(idToken.toString());
      } catch (error) {
        console.error(error);
      }

      if (!isMounted) return;

      if (!payload) {
        setAuth({ ...defaultAuth, isLoading: false });
        return;
      }

      const roles = rolesFrom(staff);

      setAuth({
        username: payload.name || '',
        userid: payload.sub || null,
        roles,
        memberships: staff.memberships,
        isPlatformAdmin: staff.is_platform_admin,
        isAdmin: roles.includes('Admin'),
        isSafetyMonitor: roles.includes('SafetyMonitor'),
        isLoading: false,
      });
    };

    loadAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  return auth;
};

export default useAuth;
